import { useState, useEffect } from 'react';

import { captureSentry } from '@/utils/sentryHelper';
import { safeParseCaseConfig, formatZodErrors, ValidationError } from '@/utils/types/validation';

import { CaseConfig } from '../types';
import { useCasesConfig } from './useCasesConfig';

export const useCaseData = (caseKey: string) => {
  const [caseConfig, setCaseConfig] = useState<CaseConfig | null>(null);
  const [assetsBaseUrl, setAssetsBaseUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | string | null>(null);
  const { getCaseMetadataByKey } = useCasesConfig();

  useEffect(() => {
    const loadCaseData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('[useCaseData] Loading case:', { caseKey });

        // Use caseKey to find metadata
        const metadata = getCaseMetadataByKey(caseKey);
        console.log('[useCaseData] Metadata found:', metadata);

        if (!metadata) {
          throw new Error(`Case ${caseKey} not found in configuration`);
        }

        // Fetch case configuration from server
        console.log('[useCaseData] Fetching from:', metadata.configUrl);
        const response = await fetch(metadata.configUrl);
        console.log('[useCaseData] Response status:', response.status, response.statusText);

        if (!response.ok) {
          throw new Error(`Failed to load case data: ${response.statusText}`);
        }

        const data = await response.json();
        console.log('[useCaseData] JSON loaded successfully');

        // Validation avec Zod
        const result = safeParseCaseConfig(data);

        if (!result.success) {
          console.error('[useCaseData] Validation errors:', result.error.errors);
          const formattedErrors = formatZodErrors(result.error);
          console.error('[useCaseData] Formatted errors:', formattedErrors);
          throw new ValidationError('Invalid case configuration', result.error);
        }

        console.log('[useCaseData] Validation successful');
        console.log('[useCaseData] Has masterclass?', !!result.data.masterclass);

        if (result.data.masterclass) {
          console.log('[useCaseData] MasterClass keys:', Object.keys(result.data.masterclass));
          console.log(
            '[useCaseData] QuestionMap keys:',
            result.data.masterclass.questionMap
              ? Object.keys(result.data.masterclass.questionMap)
              : 'N/A'
          );
        }

        setCaseConfig(result.data);
        setAssetsBaseUrl(metadata.assetsBaseUrl);
        setLoading(false);
      } catch (err) {
        if (err instanceof ValidationError) {
          setError(err);
          captureSentry({
            title: 'Case configuration validation failed',
            error: err,
            detail: { caseKey },
          });
        } else {
          const errorMessage = err instanceof Error ? err.message : 'Unknown error';
          setError(errorMessage);
          console.error('[useCaseData] Error details:', err);
          captureSentry({
            title: 'Failed to load case data',
            error: err,
            detail: { caseKey },
          });
        }
        setLoading(false);
        console.error('Error loading case data:', err);
      }
    };

    void loadCaseData();
  }, [caseKey, getCaseMetadataByKey]);

  return {
    caseConfig,
    assetsBaseUrl,
    loading,
    error,
    getSlide: (slideId: string) => caseConfig?.slides.find((s) => s.id === slideId),
    getAsset: (assetId: string) => caseConfig?.assets.find((a) => a.id === assetId),
    getTotalSlides: () => caseConfig?.slides.length || 0,
  };
};
